/**
 * Rebasing a customized-assessment draft onto a template.
 *
 * A template's working copy can hold edits nobody has published yet. A draft
 * forked from it is reverted to the published version (its baseline), so the
 * person customizes what teams actually get. Reverting a fork of the working
 * copy, rather than rebuilding it, keeps entity ids shared with it.
 *
 * Yjs merges concurrent edits but can't replay one branch onto another, so
 * updating the template compares end states: whatever the draft changed
 * relative to its baseline is written onto the working copy, field by field.
 * Those writes come last, so where the editor changed the same field since
 * the publish, the draft's value wins; anything the draft left alone keeps the
 * editor's state. Edits to a question removed in the editor have nowhere to
 * land and are left out.
 *
 * Both directions are the same operation, `applyChanges`: the revert applies
 * "working copy → published" to the fork, the update applies "published →
 * draft" to the working copy.
 *
 * Fields of the entities below are handled generically. A new top-level
 * collection in the editor's document, or a collection nested inside an
 * entity (like an answer set's choices), needs handling here; the guard test
 * in __tests__/draft-rebase.test.js fails when the editor's Binding gains a
 * collection this module doesn't list.
 */

import {
  Doc as YDoc,
  Map as YMap,
  applyUpdate,
  encodeStateAsUpdate,
  encodeStateVector,
} from 'yjs';
import { isEqual, omit } from 'lodash';

/** The document's collections this module rebases. */
export const REBASED_COLLECTIONS = [
  'pages',
  'elements',
  'answerSets',
  'config',
];

/**
 * Collections it deliberately leaves alone: a template's name and
 * description live on its record and revisions, not in `meta`, and
 * `assessment` holds nothing the editors write.
 */
export const UNREBASED_COLLECTIONS = ['meta', 'assessment'];

// Fields publishing adds to the document's own
const PAGE_EXTRAS = ['ordinal', 'elements'];
const ELEMENT_EXTRAS = ['ordinal', 'answers'];
const CHOICE_EXTRAS = ['ordinal'];

const byGuid = (list, extras) =>
  Object.fromEntries(
    (list ?? []).map((item) => [item.guid, omit(item, extras)])
  );

/**
 * A published version (`baseline`: a revision's `assessment` and `config`)
 * as entities keyed by guid. A revision published before answer sets were
 * included only shows each set's choices, through its elements; `partial`
 * says so.
 */
function publishedState({ assessment, config }) {
  const pages = assessment?.pages ?? [];
  const elements = pages.flatMap((page) => page.elements ?? []);
  const partial = !assessment?.answerSets;

  const answerSets = partial
    ? Object.fromEntries(
        elements
          .filter((element) => element.answerSetId && element.answers)
          .map((element) => [
            element.answerSetId,
            { choices: byGuid(element.answers, CHOICE_EXTRAS) },
          ])
      )
    : Object.fromEntries(
        Object.entries(assessment.answerSets).map(([guid, answerSet]) => [
          guid,
          { ...answerSet, choices: byGuid(answerSet.choices, CHOICE_EXTRAS) },
        ])
      );

  return {
    pages: byGuid(pages, PAGE_EXTRAS),
    elements: byGuid(elements, ELEMENT_EXTRAS),
    answerSets,
    config: config ?? null,
    partial,
  };
}

/**
 * A document's current state, in the shape `publishedState` gives. Read map
 * by map: `doc.toJSON()` leaves out any a freshly loaded document hasn't
 * opened yet.
 */
function docState(doc) {
  const json = (name) => doc.getMap(name).toJSON();

  return {
    pages: json('pages'),
    elements: json('elements'),
    answerSets: json('answerSets'),
    config: json('config'),
    partial: false,
  };
}

function toYValue(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return new YMap(
      Object.entries(value).map(([key, nested]) => [key, toYValue(nested)])
    );
  }

  return value;
}

const toJSON = (value) => (value instanceof YMap ? value.toJSON() : value);

/**
 * Writes onto `ymap` each field that changed from `from` to `to`, unless it
 * already has the new value. A field keeps its representation: the editor
 * nests some objects as maps (positions) and stores others as plain values
 * (topics in `config`); `nestNew` decides for a field that isn't there yet.
 */
function applyFieldChanges(ymap, from, to, { skip = [], nestNew = true } = {}) {
  new Set([...Object.keys(from), ...Object.keys(to)]).forEach((key) => {
    if (
      skip.includes(key) ||
      isEqual(from[key], to[key]) ||
      isEqual(toJSON(ymap.get(key)), to[key])
    ) {
      return;
    }

    if (to[key] === undefined) {
      ymap.delete(key);
      return;
    }

    const current = ymap.get(key);
    const nest = current instanceof YMap || (current === undefined && nestNew);
    ymap.set(key, nest ? toYValue(to[key]) : to[key]);
  });
}

/**
 * Adds, removes and changes entities in `ymap` (guid → entity) the way they
 * went from `from` to `to`. `applyEntity` writes one entity's changes.
 * `known: false` means `from` may be missing entities `ymap` has, so an
 * entity only `to` has is added only if `ymap` doesn't have it either.
 */
function applyEntityChanges(
  ymap,
  from,
  to,
  applyEntity,
  { known = true } = {}
) {
  Object.entries(to).forEach(([guid, entity]) => {
    const existing = ymap.get(guid);

    if (!(guid in from)) {
      if (!(existing instanceof YMap)) {
        ymap.set(guid, toYValue(entity));
      } else if (known) {
        applyEntity(existing, existing.toJSON(), entity);
      }
    } else if (existing instanceof YMap && !isEqual(from[guid], entity)) {
      // A question removed in the editor has no `existing` to change
      applyEntity(existing, from[guid], entity);
    }
  });

  if (known) {
    Object.keys(from)
      .filter((guid) => !(guid in to))
      .forEach((guid) => ymap.delete(guid));
  }
}

const applyFields = (ymap, from, to) => applyFieldChanges(ymap, from, to);

function applyAnswerSet(ymap, from, to, partial) {
  if (!partial) {
    applyFieldChanges(ymap, from, to, { skip: ['choices'] });
  }

  let choices = ymap.get('choices');
  if (!(choices instanceof YMap)) {
    choices = new YMap();
    ymap.set('choices', choices);
  }

  applyEntityChanges(
    choices,
    from.choices ?? {},
    to.choices ?? {},
    applyFields
  );
}

/** Writes onto `doc` what changed from state `from` to state `to`. */
function applyChanges(doc, from, to) {
  // With only part of a published version's answer sets known, a set it
  // doesn't show may still be in use; only the sets it shows are compared
  const partial = from.partial || to.partial;

  doc.transact(() => {
    applyEntityChanges(doc.getMap('pages'), from.pages, to.pages, applyFields);
    applyEntityChanges(
      doc.getMap('elements'),
      from.elements,
      to.elements,
      applyFields
    );
    applyEntityChanges(
      doc.getMap('answerSets'),
      from.answerSets,
      to.answerSets,
      (ymap, f, t) => applyAnswerSet(ymap, f, t, partial),
      { known: !partial }
    );

    if (from.config && to.config) {
      applyFieldChanges(doc.getMap('config'), from.config, to.config, {
        nestNew: false,
      });
    }
  });
}

/** Turns `doc`, a fork of a template's working copy, into its published version. */
export function revertToPublished(doc, baseline) {
  applyChanges(doc, docState(doc), publishedState(baseline));
}

/**
 * The update that writes the draft's changes onto the template's working
 * copy (`templateState`, a Yjs update), and the working copy as it will be.
 */
export function rebaseDraft(templateState, draft, baseline) {
  const template = new YDoc();
  applyUpdate(template, templateState);
  const before = encodeStateVector(template);

  applyChanges(template, publishedState(baseline), docState(draft));

  return { update: encodeStateAsUpdate(template, before), template };
}
